/**
 * UID-330 — the background, best-effort "generate → upload" orchestrator, called
 * fire-and-forget from `useUploadFlow` AFTER a file's bytes land in S3 (and, since
 * UID-335, from `usePartAttachments` after an attachment's PUT completes). It
 * renders the thumbnail in the worker, then PUTs the PNG to the DERIVED thumbnail
 * key (via the presign seam) so no DB row links them. EVERY failure is swallowed:
 * a missing render, a presign error, or a PUT error must never affect the CAD
 * upload or the batch's navigation (the part simply keeps its placeholder).
 */

import { type S3PutTicket, putFileToS3 } from '../s3Upload'
import { renderThumbnailPng } from './thumbnailQueue'

/**
 * UID-335 — takes a presign CALLBACK rather than a fixed `(id, sessionId,
 * sizeBytes)` shape, so the two call sites can each prove ownership their own
 * way: `useUploadFlow` against a staging session's `sessionId`
 * (`presignThumbnail`, security review F1 2026-09-04), `usePartAttachments`
 * against the attachment's own `partId`/`uploadId` record (no staging session
 * exists on that path). The render pipeline below is identical either way.
 */
export type ThumbnailPresign = (sizeBytes: number) => Promise<S3PutTicket>

/**
 * Render a thumbnail for `file` and, if one was produced, upload it via
 * `presign`. Resolves (never rejects) whether or not a thumbnail was made — the
 * caller `void`s it.
 */
export async function generateAndUploadThumbnail(
  file: File,
  presign: ThumbnailPresign,
): Promise<void> {
  try {
    const blob = await renderThumbnailPng(file)
    if (blob === null) {
      return
    }
    const ticket = await presign(blob.size)
    const pngFile = new File([blob], 'thumbnail.png', { type: 'image/png' })
    await putFileToS3(pngFile, ticket)
  } catch {
    // Best-effort: thumbnails never affect the upload. Swallow.
  }
}
